import { BigNumber } from 'ethers';
import { useCallback, useMemo, useState } from 'react';
import { useTransactionAdder } from '../state/transactions/hooks';
import { useDoUpdate, useOnchainInteractionsLendingPool } from './useOnchain';
import { ButtonState } from '../components/InteractionButton';
import { PermitData } from './useApprove';
import { useToNumber } from './useConverter';
import { formatFloat } from '../utils/format';
import useGenericCall from './useGenericCall';
import { useOffchainLendingPool } from './useOffchain';
import { PoolTokenType } from "impermax-sdk"


export default function useLeverage(
  approvalStateA: ButtonState, 
  approvalStateB: ButtonState, 
  invalidInput: boolean,
  amountA: BigNumber, 
  amountB: BigNumber,
  amountAMin: BigNumber, 
  amountBMin: BigNumber,
  permitDataA: PermitData, 
  permitDataB: PermitData
): [ButtonState, () => Promise<void>] {
  const onchainInteractionsLendingPool = useOnchainInteractionsLendingPool();
  const doUpdate = useDoUpdate();
  const addTransaction = useTransactionAdder();
  const [pending, setPending] = useState<boolean>(false);

  const valA = useToNumber(amountA, PoolTokenType.BorrowableA);
  const valB = useToNumber(amountB, PoolTokenType.BorrowableB);
  const data = useGenericCall(useOffchainLendingPool(), async offchainLendingPool => ({
    symbol: await offchainLendingPool.getCollateral().getSymbol(),
    symbolA: await offchainLendingPool.getBorrowableA().getSymbol(),
    symbolB: await offchainLendingPool.getBorrowableB().getSymbol(),
  }));
  const summary = data ? `Leverage ${data.symbol}: borrow ${formatFloat(valA)} ${data.symbolA} and  ${formatFloat(valB)} ${data.symbolB}` : '';
  
  const leverageState: ButtonState = useMemo(() => {
    if (invalidInput) return ButtonState.Disabled;
    if (approvalStateA != ButtonState.Done || approvalStateB != ButtonState.Done) return ButtonState.Disabled;
    if (pending) return ButtonState.Pending;
    return ButtonState.Ready;
  }, [approvalStateA, approvalStateB, pending]);

  const leverage = useCallback(async (): Promise<void> => {
    if (leverageState !== ButtonState.Ready) return;
    setPending(true);
    try {
      await onchainInteractionsLendingPool.leverage(amountA, amountB, amountAMin, amountBMin, permitDataA, permitDataB, (hash: string) => {
        addTransaction({ hash }, { summary });
      });
      doUpdate();
    }
    finally {
      setPending(false);
    }
  }, [onchainInteractionsLendingPool, addTransaction, amountA, amountB, amountAMin, amountBMin, permitDataA, permitDataB, leverageState]);

  return [leverageState, leverage];
}